import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Volume2, VolumeX, Disc3, Loader2, Settings, ChevronDown, ChevronLeft, ChevronRight, Sparkles, Music2, Search } from 'lucide-react';
import * as Tone from 'tone';
import DeckCore from './DeckCore';
import MixerControls from './MixerControls';
import TrackLoader from './TrackLoader';
import SidePanel from './SidePanel';
import EQKnobs from './EQKnobs';
import LoopControls from './LoopControls';
import TrackQueue from './TrackQueue';
import './DJMixer.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DJMixer = ({ isOpen, onClose, crate, tracks = [] }) => {
  // Mixer mode: 'basic', 'intermediate', 'advanced'
  const [mode, setMode] = useState('basic');
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  
  // Audio context state
  const [audioContextStarted, setAudioContextStarted] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  
  // Side panel states
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  
  // Deck states
  const [deckA, setDeckA] = useState({
    track: null,
    isPlaying: false,
    isLoading: false,
    volume: 0.8,
    pitch: 0, // -8 to +8 percent
    position: 0,
    duration: 0,
    eq: { low: 0, mid: 0, high: 0 },
    cuePoint: 0,
    bpm: null,
    waveformReady: false,
    loop: null
  });
  
  const [deckB, setDeckB] = useState({
    track: null,
    isPlaying: false,
    isLoading: false,
    volume: 0.8,
    pitch: 0, // -8 to +8 percent
    position: 0,
    duration: 0,
    eq: { low: 0, mid: 0, high: 0 },
    cuePoint: 0,
    bpm: null,
    waveformReady: false,
    loop: null
  });
  
  // Crossfader (0 = full A, 1 = full B, 0.5 = center)
  const [crossfader, setCrossfader] = useState(0.5);
  const [masterVolume, setMasterVolume] = useState(0.8);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [isMasterMuted, setIsMasterMuted] = useState(false);
  
  // Track loader state
  const [showTrackLoader, setShowTrackLoader] = useState(false);
  const [loadingForDeck, setLoadingForDeck] = useState(null);
  
  // All crates for track queue
  const [allCrates, setAllCrates] = useState([]);
  
  // Audio refs
  const masterGainRef = useRef(null);
  const deckAGainRef = useRef(null);
  const deckBGainRef = useRef(null);
  
  // Initialize audio context
  const initializeAudioContext = useCallback(async () => {
    if (audioContextStarted) return;
    
    setIsInitializing(true);
    try {
      await Tone.start();
      masterGainRef.current = new Tone.Gain(masterVolume).toDestination();
      deckAGainRef.current = new Tone.Gain(deckA.volume).connect(masterGainRef.current);
      deckBGainRef.current = new Tone.Gain(deckB.volume).connect(masterGainRef.current);
      setAudioContextStarted(true);
    } catch (error) {
      console.error('Failed to initialize audio:', error);
    } finally {
      setIsInitializing(false);
    }
  }, [audioContextStarted, masterVolume, deckA.volume, deckB.volume]);
  
  // Crossfader effect
  useEffect(() => {
    if (!audioContextStarted) return;
    const fadeA = Math.cos(crossfader * Math.PI / 2);
    const fadeB = Math.sin(crossfader * Math.PI / 2);
    if (deckAGainRef.current) deckAGainRef.current.gain.value = deckA.volume * fadeA;
    if (deckBGainRef.current) deckBGainRef.current.gain.value = deckB.volume * fadeB;
  }, [crossfader, deckA.volume, deckB.volume, audioContextStarted]);
  
  // Master volume
  useEffect(() => {
    if (masterGainRef.current) {
      masterGainRef.current.gain.value = isMasterMuted ? 0 : masterVolume;
    }
  }, [masterVolume, isMasterMuted]);
  
  // Fetch all crates when mixer opens
  useEffect(() => {
    const fetchCrates = async () => {
      try {
        const token = localStorage.getItem('beatseek_token');
        const response = await fetch(`${BACKEND_URL}/api/crates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAllCrates(data.crates || []);
        }
      } catch (error) {
        console.error('Failed to fetch crates:', error);
      }
    };
    
    if (isOpen) {
      fetchCrates();
    }
  }, [isOpen]);
  
  // Load track to deck
  const loadTrackToDeck = useCallback(async (track, deck) => {
    const setDeckState = deck === 'A' ? setDeckA : setDeckB;
    setDeckState(prev => ({ ...prev, isLoading: true, track }));
    
    try {
      // ALWAYS use the backend proxy to avoid CORS issues
      // The backend will use existing preview_url if available, or search for one
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/dj-mixer/get-audio-url`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ track })
      });
      
      let audioUrl = null;
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.audio_url) {
          audioUrl = data.audio_url;
          console.log(`DJ Mixer: Got audio from ${data.source}`, data.matched_track || '');
        } else {
          console.warn('DJ Mixer: No audio URL returned', data);
        }
      } else {
        console.error('DJ Mixer: Failed to get audio URL', await response.text());
      }
      
      setDeckState(prev => ({ 
        ...prev, 
        isLoading: false,
        audioUrl,
        bpm: track.bpm || null,
        waveformReady: !!audioUrl
      }));
    } catch (error) {
      console.error(`Failed to load track to Deck ${deck}:`, error);
      setDeckState(prev => ({ ...prev, isLoading: false }));
    }
    
    setShowTrackLoader(false);
  }, []);
  
  const togglePlay = useCallback((deck) => {
    const setDeckState = deck === 'A' ? setDeckA : setDeckB;
    setDeckState(prev => ({ ...prev, isPlaying: !prev.isPlaying }));
  }, []);
  
  const setDeckVolume = useCallback((deck, volume) => {
    const setDeckState = deck === 'A' ? setDeckA : setDeckB;
    setDeckState(prev => ({ ...prev, volume }));
  }, []);
  
  const setDeckPitch = useCallback((deck, pitch) => {
    const setDeckState = deck === 'A' ? setDeckA : setDeckB;
    setDeckState(prev => ({ ...prev, pitch }));
  }, []);
  
  const setDeckEQ = useCallback((deck, band, value) => {
    const setDeckState = deck === 'A' ? setDeckA : setDeckB;
    setDeckState(prev => ({ ...prev, eq: { ...prev.eq, [band]: value } }));
  }, []);
  
  const setCuePoint = useCallback((deck, position) => {
    const setDeckState = deck === 'A' ? setDeckA : setDeckB;
    setDeckState(prev => ({ ...prev, cuePoint: position }));
  }, []);
  
  const updatePosition = useCallback((deck, position, duration) => {
    const setDeckState = deck === 'A' ? setDeckA : setDeckB;
    setDeckState(prev => ({ ...prev, position, duration }));
  }, []);
  
  const setLoopData = useCallback((deck, loop) => {
    const setDeckState = deck === 'A' ? setDeckA : setDeckB;
    setDeckState(prev => ({ ...prev, loop }));
  }, []);
  
  // Sync BPM - adjust pitch to match target BPM
  const syncBPM = useCallback((sourceDeck, targetDeck) => {
    const source = sourceDeck === 'A' ? deckA : deckB;
    const target = targetDeck === 'A' ? deckA : deckB;
    const setTargetState = targetDeck === 'A' ? setDeckA : setDeckB;
    
    if (!source.bpm || !target.bpm) {
      console.log(`SYNC: Cannot sync - Source BPM: ${source.bpm}, Target BPM: ${target.bpm}`);
      return;
    }
    
    // Calculate pitch adjustment needed to match source BPM
    // Formula: ((sourceBPM / targetBPM) - 1) * 100
    // This gives us the percentage speed change needed for target to match source
    const pitchAdjust = ((source.bpm / target.bpm) - 1) * 100;
    
    // Clamp to ±8% range
    const clampedPitch = Math.max(-8, Math.min(8, pitchAdjust));
    
    console.log(`SYNC: Adjusting Deck ${targetDeck} pitch to ${clampedPitch.toFixed(2)}% to match ${source.bpm} BPM (from ${target.bpm} BPM)`);
    
    setTargetState(prev => ({ ...prev, pitch: clampedPitch }));
  }, [deckA, deckB]);
  
  // Sync Deck B to match Deck A's BPM
  const syncToDeckA = useCallback(() => {
    if (syncEnabled) {
      // If sync is already on, turn it off and reset Deck B pitch
      setSyncEnabled(false);
      setDeckB(prev => ({ ...prev, pitch: 0 }));
      console.log('SYNC: Disabled, resetting Deck B pitch to 0');
    } else {
      // Turn sync on and apply BPM matching
      syncBPM('A', 'B');
      setSyncEnabled(true);
      console.log('SYNC: Enabled');
    }
  }, [syncBPM, syncEnabled]);
  
  // Sync Deck A to match Deck B's BPM
  const syncToDeckB = useCallback(() => syncBPM('B', 'A'), [syncBPM]);
  
  const openTrackLoader = useCallback((deck) => {
    setLoadingForDeck(deck);
    setShowTrackLoader(true);
  }, []);
  
  const ejectTrack = useCallback((deck) => {
    const setDeckState = deck === 'A' ? setDeckA : setDeckB;
    setDeckState({
      track: null, isPlaying: false, isLoading: false, volume: 0.8, pitch: 0,
      position: 0, duration: 0, eq: { low: 0, mid: 0, high: 0 },
      cuePoint: 0, bpm: null, waveformReady: false, audioUrl: null, loop: null
    });
  }, []);
  
  if (!isOpen) return null;
  
  return (
    <div className="dj-mixer-overlay" onClick={onClose} data-testid="dj-mixer-overlay">
      <div className="dj-mixer-container" onClick={(e) => e.stopPropagation()} data-testid="dj-mixer-container">
        
        {/* Left Side Panel - Slides OUTSIDE to the left */}
        {mode === 'advanced' && (
          <SidePanel 
            isOpen={leftPanelOpen}
            side="left"
            deck="A"
            mode={mode}
            deckState={deckA}
            tracks={tracks}
            audioContextStarted={audioContextStarted}
            audioContext={Tone.context?.rawContext}
            onEQChange={(band, val) => setDeckEQ('A', band, val)}
            onLoadTrack={(track) => loadTrackToDeck(track, 'A')}
            onToggle={() => setLeftPanelOpen(!leftPanelOpen)}
          />
        )}
        
        {/* Main Mixer Content */}
        <div className={`dj-mixer-main-wrapper ${leftPanelOpen ? 'left-panel-open' : ''} ${rightPanelOpen ? 'right-panel-open' : ''} ${leftPanelOpen && rightPanelOpen ? 'both-panels-open' : ''}`}>
          {/* Header */}
          <div className="dj-mixer-header">
            <div className="header-left">
              <Disc3 size={20} className="mixer-logo" />
              <div className="header-branding">
                <span className="brand-beatseek">BEATSEEK</span>
                <span className="brand-djmixer">DJ MIXER</span>
              </div>
              {crate && <span className="crate-name">• {crate.name}</span>}
            </div>
            
            <div className="header-right">
              {/* Mode Selector - only show when audio is ready */}
              {audioContextStarted && (
                <div className="mode-selector">
                  <button className="mode-btn" onClick={() => setShowModeDropdown(!showModeDropdown)} data-testid="mode-selector-btn">
                    <Settings size={14} />
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    <ChevronDown size={12} />
                  </button>
                  {showModeDropdown && (
                    <div className="mode-dropdown" data-testid="mode-dropdown">
                      {['basic', 'intermediate', 'advanced'].map((m) => (
                        <button 
                          key={m}
                          className={mode === m ? 'active' : ''}
                          onClick={() => { setMode(m); setShowModeDropdown(false); }}
                          data-testid={`mode-option-${m}`}
                        >
                          {m.charAt(0).toUpperCase() + m.slice(1)}
                          <span className="mode-desc">
                            {m === 'basic' ? 'Play, Volume, Crossfade' : 
                             m === 'intermediate' ? '+ EQ, Cue, Loop' : '+ FX, Samples, Search'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="master-volume">
                <button className="mute-btn" onClick={() => setIsMasterMuted(!isMasterMuted)} data-testid="master-mute-btn">
                  {isMasterMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
                <input 
                  type="range" min="0" max="1" step="0.01" value={masterVolume}
                  onChange={(e) => setMasterVolume(parseFloat(e.target.value))}
                  className="master-slider"
                  data-testid="master-volume-slider"
                />
                <span className="master-label">Master</span>
              </div>
              <button className="close-btn" onClick={onClose} data-testid="dj-mixer-close-btn"><X size={20} /></button>
            </div>
          </div>
          
          {/* Audio Init Overlay */}
          {!audioContextStarted && (
            <div className="audio-init-overlay" data-testid="audio-init-overlay">
              <button className="init-audio-btn" onClick={initializeAudioContext} disabled={isInitializing} data-testid="init-audio-btn">
                {isInitializing ? <><Loader2 size={22} className="spinning" /> Initializing...</> 
                  : <><Disc3 size={22} /> Start Audio Engine</>}
              </button>
              <p className="init-hint">Browser requires interaction to enable audio</p>
              
              {/* Playback Notice */}
              <div className="playback-notice">
                <div className="notice-header">⚠️ PLAYBACK NOTICE</div>
                <p className="notice-text">
                  Most streaming tracks are <strong>30-second previews</strong> powered by Deezer & Spotify.
                </p>
                <p className="notice-tip">
                  Connect your <strong>Spotify Premium</strong> for best results.
                </p>
                <div className="notice-options">
                  <span>Full Track Playback:</span>
                  <ul>
                    <li>• Upload local audio files (MP3, WAV, FLAC)</li>
                    <li>• Connect personal SoundCloud Go+ via Dashboard</li>
                  </ul>
                </div>
                <div className="notice-coming-soon">
                  <span className="coming-soon-label">COMING SOON</span>
                  <p>More partnerships & integrations for full track playback in the BEATSEEK Mixer</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Main Mixer Layout - Non-scrolling with EQ and Loops in main view */}
          <div className={`mixer-layout ${!audioContextStarted ? 'disabled' : ''}`} data-testid="mixer-layout">
            
            {/* Main Content Area */}
            <div className={`mixer-main-content ${mode === 'basic' ? 'basic-mode' : ''}`}>
              {/* Decks Row - Main row with Deck A, Mixer Controls, Deck B */}
              <div className="decks-row">
                <DeckCore 
                  deck="A" state={deckA} mode={mode}
                  onPlay={() => togglePlay('A')}
                  onVolumeChange={(v) => setDeckVolume('A', v)}
                  onPitchChange={(p) => setDeckPitch('A', p)}
                  onCuePoint={(p) => setCuePoint('A', p)}
                  onPositionUpdate={(p, d) => updatePosition('A', p, d)}
                  onLoadTrack={() => openTrackLoader('A')}
                  onEject={() => ejectTrack('A')}
                  audioContextStarted={audioContextStarted}
                  masterVolume={isMasterMuted ? 0 : masterVolume}
                  crossfaderValue={crossfader}
                />
                
                <MixerControls 
                  crossfader={crossfader}
                  onCrossfaderChange={setCrossfader}
                  deckA={deckA}
                  deckB={deckB}
                  mode={mode}
                  syncEnabled={syncEnabled}
                  onSyncToDeckA={syncToDeckA}
                  onSyncToDeckB={syncToDeckB}
                />
                
                <DeckCore 
                  deck="B" state={deckB} mode={mode}
                  onPlay={() => togglePlay('B')}
                  onVolumeChange={(v) => setDeckVolume('B', v)}
                  onPitchChange={(p) => setDeckPitch('B', p)}
                  onCuePoint={(p) => setCuePoint('B', p)}
                  onPositionUpdate={(p, d) => updatePosition('B', p, d)}
                  onLoadTrack={() => openTrackLoader('B')}
                  onEject={() => ejectTrack('B')}
                  audioContextStarted={audioContextStarted}
                  masterVolume={isMasterMuted ? 0 : masterVolume}
                  crossfaderValue={crossfader}
                />
              </div>
              
              {/* EQ + Loop Combined Row (Intermediate+) - Below Decks */}
              {mode !== 'basic' && (
                <div className="eq-loop-combined-row" data-testid="eq-loop-row">
                  {/* Deck A: EQ + Loop */}
                  <div className="deck-eq-loop deck-a">
                    <EQKnobs 
                      deck="A"
                      eq={deckA.eq}
                      onChange={(band, val) => setDeckEQ('A', band, val)}
                    />
                    <div className="eq-loop-separator" />
                    <LoopControls
                      deck="A"
                      isEnabled={audioContextStarted && !!deckA.track}
                      bpm={deckA.bpm}
                      position={deckA.position}
                      duration={deckA.duration}
                      onLoopChange={(loop) => setLoopData('A', loop)}
                    />
                  </div>
                  
                  {/* Center spacer */}
                  <div className="eq-loop-center" />
                  
                  {/* Deck B: EQ + Loop */}
                  <div className="deck-eq-loop deck-b">
                    <EQKnobs 
                      deck="B"
                      eq={deckB.eq}
                      onChange={(band, val) => setDeckEQ('B', band, val)}
                    />
                    <div className="eq-loop-separator" />
                    <LoopControls
                      deck="B"
                      isEnabled={audioContextStarted && !!deckB.track}
                      bpm={deckB.bpm}
                      position={deckB.position}
                      duration={deckB.duration}
                      onLoopChange={(loop) => setLoopData('B', loop)}
                    />
                  </div>
                </div>
              )}
              
              {/* Track Queue - Below EQ/Loop */}
              {mode !== 'basic' && (
                <TrackQueue
                  tracks={tracks}
                  crates={allCrates}
                  onLoadToDeckA={(track) => loadTrackToDeck(track, 'A')}
                  onLoadToDeckB={(track) => loadTrackToDeck(track, 'B')}
                  isEnabled={audioContextStarted}
                  deckAPlaying={deckA.isPlaying}
                  deckBPlaying={deckB.isPlaying}
                />
              )}
            </div>
          </div>
        </div>
        
        {/* Right Side Panel - Slides OUTSIDE to the right */}
        {mode === 'advanced' && (
          <SidePanel 
            isOpen={rightPanelOpen}
            side="right"
            deck="B"
            mode={mode}
            deckState={deckB}
            tracks={tracks}
            audioContextStarted={audioContextStarted}
            audioContext={Tone.context?.rawContext}
            onEQChange={(band, val) => setDeckEQ('B', band, val)}
            onLoadTrack={(track) => loadTrackToDeck(track, 'B')}
            onToggle={() => setRightPanelOpen(!rightPanelOpen)}
          />
        )}
        
        {/* Track Loader Modal */}
        {showTrackLoader && (
          <TrackLoader 
            tracks={tracks}
            onSelect={(track) => loadTrackToDeck(track, loadingForDeck)}
            onClose={() => setShowTrackLoader(false)}
            targetDeck={loadingForDeck}
          />
        )}
      </div>
    </div>
  );
};

export default DJMixer;
